import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Create axios instance for copy agent API calls
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
apiClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

export interface CopyAgentSession {
  session_id: string;
  project_id: number;
  current_step: number;
  step_0_result: any;
  context: any;
  status: string;
}

export interface CopyAgentStepResult {
  success: boolean;
  step_result: any;
  message: string;
}

export interface CopyAgentChatResult {
  success: boolean;
  result: {
    assistant_message: string;
    confidence?: string;
    error?: string;
  };
}

export interface ProjectData {
  project: {
    id: number;
    name: string;
    brand: string;
    product_info: string;
    amazon_link: string;
    website_link: string;
    instagram_link: string;
    target_audience: string;
    tone: string;
    goals: string;
    listing_elements: string[];
    not_working: string[];
    product_category: string[];
    primary_image_url?: string;
    created_at?: string;
  };
  creative_brief: any;
  research_reports: Record<string, any>;
  scraped_data: Record<string, any>;
}

export interface ListingImagesResult {
  success: boolean;
  images_result: any;
  message: string;
}

export interface StorefrontResult {
  success: boolean;
  storefront_result: any;
  message: string;
}

export interface APlusResult {
  success: boolean;
  a_plus_result: any;
  message: string;
}

// Copy Agent API Functions
export const copyAgentApi = {
  // Start a new copy agent session
  startSession: async (projectId: number): Promise<CopyAgentSession> => {
    const response = await apiClient.post('/api/copy-agent/start-session', {
      project_id: projectId
    });
    return response.data.session_data;
  },

  // Execute a specific copy agent step
  executeStep: async (
    sessionId: string, 
    stepNumber: number, 
    userInput?: string
  ): Promise<CopyAgentStepResult> => {
    const response = await apiClient.post('/api/copy-agent/execute-step', {
      session_id: sessionId,
      step_number: stepNumber,
      user_input: userInput
    });
    return response.data;
  },

  // General-purpose chat handler
  chat: async (
    sessionId: string,
    userMessage: string
  ): Promise<CopyAgentChatResult> => {
    const response = await apiClient.post('/api/copy-agent/chat', {
      session_id: sessionId,
      user_message: userMessage
    });
    return response.data;
  },

  // Get project data for copy agent
  getProjectData: async (projectId: number): Promise<ProjectData> => {
    const response = await apiClient.get(`/api/copy-agent/project-data/${projectId}`);
    return response.data.data;
  },

  // Generate listing images
  generateListingImages: async (projectId: number): Promise<ListingImagesResult> => {
    const response = await apiClient.post('/api/copy-agent/generate-listing-images', {
      project_id: projectId
    });
    return response.data;
  },

  // Generate storefront
  generateStorefront: async (
    projectId: number, 
    catalogSize: 'small' | 'medium' | 'large' = 'medium'
  ): Promise<StorefrontResult> => {
    const response = await apiClient.post('/api/copy-agent/generate-storefront', {
      project_id: projectId,
      catalog_size: catalogSize
    });
    return response.data;
  },

  // Generate A++ content
  generateAPlus: async (projectId: number): Promise<APlusResult> => {
    const response = await apiClient.post('/api/copy-agent/generate-a-plus', {
      project_id: projectId
    });
    return response.data;
  }
};

// React Query hooks for copy agent
export const useCopyAgentSession = (projectId: number | null) => {
  return useQuery({
    queryKey: ['copyAgentSession', projectId],
    queryFn: () => copyAgentApi.startSession(projectId!),
    enabled: !!projectId,
  });
};

export const useCopyAgentStep = (
  sessionId: string | null,
  stepNumber: number | null,
  userInput?: string
) => {
  return useMutation({
    mutationFn: () => copyAgentApi.executeStep(sessionId!, stepNumber!, userInput),
    onSuccess: () => {},
    onError: () => {}
  });
};

export const useProjectData = (projectId: number | null) => {
  return useQuery({
    queryKey: ['projectData', projectId],
    queryFn: () => copyAgentApi.getProjectData(projectId!),
    enabled: !!projectId,
  });
};

export const useGenerateListingImages = () => {
  return useMutation({
    mutationFn: (projectId: number) => copyAgentApi.generateListingImages(projectId),
    onSuccess: () => {},
    onError: () => {}
  });
};

export const useGenerateStorefront = () => {
  return useMutation({
    mutationFn: ({ projectId, catalogSize }: { projectId: number; catalogSize: 'small' | 'medium' | 'large' }) => 
      copyAgentApi.generateStorefront(projectId, catalogSize),
    onSuccess: () => {},
    onError: () => {}
  });
};

export const useGenerateAPlus = () => {
  return useMutation({
    mutationFn: (projectId: number) => copyAgentApi.generateAPlus(projectId),
    onSuccess: () => {},
    onError: () => {}
  });
};

// Import React Query hooks
import { useQuery, useMutation } from '@tanstack/react-query';
